import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { Layout } from 'components/Layout';
import { getPageStaticProps } from 'lib/dato-request';

import { CheckoutScreen1 } from '@/components/Checkout/B2BCheckout/CheckoutScreen1';
import CheckoutScreen2 from '@/components/Checkout/B2BCheckout/CheckoutScreen2';
import { CheckoutScreen3 } from '@/components/Checkout/B2BCheckout/CheckoutScreen3';
import ProgressBar from '@/components/Checkout/components/ProgressBar';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import CheckoutScreen from '@/components/Checkout/CheckoutScreen';
import { Base64 } from 'js-base64';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { BlockKlaviyo, useAnalytics } from '@/analytics/AnalyticsProvider';
import { AccountSetupCanceled, CheckoutCanceled, NewParentCancelTrialSignup, PurchaseCanceled } from '@/analytics/events';
import { PAYPAL_CLIENT_ID, PARENT_CHECKOUT_PATH } from '@/lib/defaults';
import { buildPublicSeoProps, PUBLIC_SITE_ORIGIN } from '@/lib/public-site-policy';

const testimonials = [
    {
        text: (
            <div>
                Before Muzology I was getting Ds. After Muzology, <span className='font-semibold'>I’m getting As and Bs.</span> It made me like math more!
            </div>
        ),
        name: '6th grader (Tampa, FL)'
    },
    {
        text: (
            <div>
                <span className='font-semibold'>Muzology was a game changer for my son.</span> The music videos helped him learn and remember. Finally, math is
                not the enemy!
            </div>
        ),
        name: 'Susie L, Parent and psychologist (Miami, FL)'
    }
];

const Checkout = ({ data }) => {
    const analytics = useAnalytics();
    const { page, site } = data;
    const { components, seo } = page;
    const seoProps = buildPublicSeoProps(PUBLIC_SITE_ORIGIN, PARENT_CHECKOUT_PATH, seo ?? {});

    useEffect(() => {
        analytics.page('Checkout');
    }, [analytics]);

    // get the email from the query string
    const router = useRouter();
    const { email, stage, token, client: clientSecretEncoded } = router.query ?? {};

    const sessionId = useMemo(() => {
        if (clientSecretEncoded) return Base64.decode(clientSecretEncoded as string);
        return null;
    }, [clientSecretEncoded]);

    // get the stage from the query string
    const activeTab = useMemo(() => parseInt((stage ?? '0') as string, 10), [stage]);
    const [isLoading, setLoading] = useState(false);

    const [activeTab2, setActiveTab] = useState(stage ? parseInt(stage as string, 10) : 0);

    const [selectedParentPlan, setParentPlan] = useState(0);

    // const [sessionId, setId] = useState(null);

    const nextStage = (args) => {
        // save the client secret
        const query = {
            ...router.query,
            ...(args ?? {})
        };
        // perform a shallow push
        return router.push({ pathname: '/checkout', query }, undefined, {
            shallow: true,
            scroll: true
        });
    };

    const stages = useMemo(() => {
        const images1 = components[0].gallery.slice(0, 2);
        const images2 = components[0].gallery.slice(2, 4);
        const awards1 = components[1].gallery.slice(0, 3);
        const awards2 = components[1].gallery.slice(0, 3);
        const imagesMobile = components[0].gallery.slice(4, 6);

        const laptopImage = components[2].gallery[0];
        return [
            {},
            {
                title: 'Keep Achieving with Muzology!',
                subHeader: '',
                mainImage: null,
                logoImages: images2,
                awardImages: awards2,
                imagesMobile,
                testimonial: testimonials[1]
            }
        ];
    }, [components]);

    const currentStage = stages[activeTab];

    // if (activeTab === 0) {
    //     const images1 = components[0].gallery.slice(0, 2);
    //     const images2 = components[0].gallery.slice(2, 4);
    //     const awards1 = components[1].gallery.slice(0, 3);
    //     const awards2 = components[1].gallery.slice(0, 3);
    //


    let backgroundClassName = 'bg-home-background w-full min-h-[100vh] bg-fixed bg-no-repeat bg-cover  ';
    if (activeTab === 1) backgroundClassName = 'bg-[#F7FAFC] sm:bg-checkout-timestables-background';

    // backgroundClassName = 'bg-checkout-background-three';
    const cancelCheckout = () => {
        analytics.track(CheckoutCanceled, { email, token, stage }, BlockKlaviyo);

        // if (!stage || stage === '0') {
        //     analytics.track(AccountSetupCanceled, { email, token, stage });
        // } else {
        //     analytics.track(PurchaseCanceled, { email, token, stage });
        // }
        //
        // analytics.track(NewParentCancelTrialSignup, { email, token, stage });

        // remove the checkout state from local storage
        localStorage.removeItem('checkout-state');
        // go to the parents page
        router.push('/parents', undefined, { shallow: true });
    };

    const checkoutElements = useMemo(() => {
        return [
            <CheckoutScreen2
                key='checkout-stage-2'
                nextStage={nextStage}
                setParentPlan={setParentPlan}
            />,
            <CheckoutScreen
                key='checkout-stage-3'
                testimonial={currentStage.testimonial}
                title={currentStage.title}
                subHeader={currentStage.subHeader}
                text=''
                activeTab={activeTab}
                awardImages={components[3].gallery}
            >
                <LoadingOverlay show={isLoading} className='z-50 bg-red-300' style={{ background: '#00000060' }} />
                <div>
                    <CheckoutScreen3
                        key='screen2'
                        email={email}
                        isLoading={isLoading}
                        selectedParentPlan={selectedParentPlan}
                        setLoading={setLoading}
                        showPlanButtons={true}
                        token={token}
                        sessionId={sessionId}
                    />
                </div>
            </CheckoutScreen>
        ];
    }, [activeTab, components, currentStage.subHeader, currentStage.testimonial, currentStage.title, email, isLoading, nextStage, selectedParentPlan, sessionId, token]);

    return (
        <Layout hideNavbar={true} hideFooter={true}>
            <NextSeo {...seoProps} />
            <Head>
                <meta name='keywords' content={page?.keywords} />
            </Head>

            <PayPalScriptProvider
                options={{
                    clientId: PAYPAL_CLIENT_ID,
                    intent: 'subscription',
                    vault: true,
                    components: 'buttons',
                    currency: 'USD'
                }}
            >
                <div key={email as string} className={`${backgroundClassName} bg-cover h-screen sm:pt-5 py-0 sm:py-5 lg:px-8 overflow-y-auto relative`}>

                    <div className='max-w-[1274px] mx-auto grow '>
                        <div className='sm:pb-8' />
                        {checkoutElements[activeTab]}
                    </div>
                </div>
            </PayPalScriptProvider>
        </Layout>
    );
};

export async function getStaticProps({ preview = false }) {
    return getPageStaticProps('Checkout');
}

export default Checkout;
